import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import { toggleReconnectingState, disconnectSession } from 'actions/opentok'
import { updateError } from 'interview/actions/sequenced-interview'
import { updateState } from 'interview/actions/candidate'
import { mapLanguageToComponentProps } from 'interview/i18n'
import { LoadingIndicator } from 'components/styled/loading-indicator'

const propTypes = {
  i18n: PropTypes.shape({
    sequencedInterview: PropTypes.shape({}),
  }).isRequired,
  interview: PropTypes.shape({
    additionalFormFields: PropTypes.arrayOf(PropTypes.object),
    formSetup: PropTypes.shape({}),
    company: PropTypes.shape({
      privacyPolicy: PropTypes.string,
    }),
  }).isRequired,
  candidate: PropTypes.shape({
    uuid: PropTypes.string,
    completeUrl: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    additionalFieldResponses: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  candidateState: PropTypes.string,
  error: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  stateChangedAt: PropTypes.string,
  websocketError: PropTypes.string,
  session: PropTypes.shape({
    currentState: PropTypes.string,
  }),
}

const defaultProps = {
  error: '',
  candidateState: '',
  stateChangedAt: '',
  websocketError: '',
  children: undefined,
  session: undefined,
}

const mapStateToProps = state => ({
  error: state.interview.candidate.error,
  streaming: state.global.opentok.streaming,
  interview: state.interview.jobOpening,
  candidate: state.candidate,
  candidateState: state.interview.candidate.state,
  stateChangedAt: state.interview.candidate.stateChangedAt,
  websocketError: state.global.websocketError,
  session: state.global.opentok.session,
})

const TWO_MIN = 120000

export class TimedInterviewWrapper extends Component {
  componentDidMount() {
    const {
      candidate: { uuid },
      candidateState,
      error,
      dispatch,
      session,
    } = this.props
    const sessionConnected = session && session.currentState !== 'disconnected'

    if (error && error === 'notFound') {
      browserHistory.replace('/closed')
    } else if (candidateState === 'completed') {
      dispatch(updateState(uuid))
    } else if (candidateState === 'visited') {
      // disconnect opentok session if moved back to form view
      if (sessionConnected) {
        dispatch(disconnectSession())
      }
      browserHistory.replace(`/sequenced-interview/${uuid}`)
    } else if (!this.hasCandidateAnsweredToRequired()) {
      dispatch(updateState(uuid, undefined, false, true))
    } else if (candidateState && candidateState === 'networktest') {
      browserHistory.replace(`/sequenced-interview/${uuid}/test`)
    } else if (candidateState && candidateState.includes('question')) {
      browserHistory.replace(`/sequenced-interview/${uuid}/questions`)
    }
  }

  componentDidUpdate(prevProps) {
    const {
      candidateState,
      stateChangedAt,
      candidate: { uuid, completeUrl },
      websocketError,
      dispatch,
      session,
      error,
    } = this.props
    const sessionConnected = session && session.currentState !== 'disconnected'
    if (
      stateChangedAt !== prevProps.stateChangedAt &&
      candidateState === 'completed'
    ) {
      if (completeUrl) {
        window.location.href = completeUrl
      } else {
        browserHistory.push('/sequenced-interview/finished')
      }
    }
    if (candidateState !== prevProps.candidateState) {
      if (candidateState === 'visited') {
        // disconnect opentok session if moved back to form view
        if (sessionConnected) {
          dispatch(disconnectSession())
        }
        browserHistory.replace(`/sequenced-interview/${uuid}`)
      } else if (candidateState.includes('question')) {
        browserHistory.replace(`/sequenced-interview/${uuid}/questions`)
      }
    }

    if (error !== prevProps.error && error === 'notFound') {
      browserHistory.replace('/closed')
    }

    if (websocketError !== prevProps.websocketError) {
      if (websocketError.length > 0) {
        // set reconnecting state to true if websocket
        // is not connected when trying to emit a message
        clearTimeout(this.websocketErrorTimeout)
        dispatch(toggleReconnectingState(true))

        this.websocketErrorTimeout = setTimeout(() => {
          // if not re-connected in two minutes, disconnect the session and show error
          dispatch(toggleReconnectingState(false))
          dispatch(updateError('generalError'))
          if (sessionConnected) {
            dispatch(disconnectSession())
          }
          // Sentry error log
          if (typeof Raven !== 'undefined') {
            Raven.captureException(
              new Error('Failed to reconnect the websocket in 2 minutes'),
              {
                extra: { candidateUuid: uuid },
              }
            )
          }
        }, TWO_MIN)
      } else {
        // set reconnecting to false when websocket has
        // reconnected if timeout has not already disconnected session
        dispatch(toggleReconnectingState(false))

        if (this.websocketErrorTimeout) {
          clearTimeout(this.websocketErrorTimeout)
        }
      }
    }
  }

  getFieldName(key) {
    switch (key) {
      case 'links':
        return 'link'
      case 'phoneNumber':
        return 'phone'
      default:
        return key
    }
  }

  hasCandidateAnsweredToRequired() {
    const {
      interview: { additionalFormFields, formSetup },
      candidate,
    } = this.props
    let result = true

    if (!candidate.agreeTOS) {
      return false
    }

    Object.keys(formSetup).forEach(key => {
      if (formSetup[key].visible && formSetup[key].required) {
        const field = this.getFieldName(key)
        if (!candidate[field]) {
          result = false
        }

        // for attachments, check if first item in array exists
        if (field === 'attachments' && !candidate[field][0]) {
          result = false
        }
      }
    })

    if (additionalFormFields.length > 0) {
      const requiredAdditionalFields = additionalFormFields.filter(
        f => f.fieldSetup.visible && f.fieldSetup.required
      )

      requiredAdditionalFields.forEach(field => {
        const response =
          candidate.additionalFieldResponses &&
          candidate.additionalFieldResponses.find(f => f.fieldId === field._id)

        if (!response) {
          result = false
        }
      })
    }
    return result
  }

  render() {
    const { children, error, websocketError } = this.props

    // render spinner if candidate has not answered to all required until candidate state
    // is changed and they are redirected to form

    const hasAnsweredToRequiredFields = this.hasCandidateAnsweredToRequired()
    if (!hasAnsweredToRequiredFields && !error && !websocketError) {
      return <LoadingIndicator />
    }

    return children
  }
}

TimedInterviewWrapper.propTypes = propTypes
TimedInterviewWrapper.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  TimedInterviewWrapper
)
